import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import API from "lib/api/API2";
import { styled } from "@mui/material/styles";
import ServerRowMenu from "views/Components/ServerRowMenu";
import ServerSelectionControls from "views/Components/ServerSelectionControls";

const PREFIX = "ServerTable";

const classes = {
  actions: `${PREFIX}-actions`,
  dataGrid: `${PREFIX}-dataGrid`,
  errorContainer: `${PREFIX}-errorContainer`,
  loadingOverlay: `${PREFIX}-loadingOverlay`,
  paginationContainer: `${PREFIX}-paginationContainer`,
  tableCell: `${PREFIX}-tableCell`,
  tooltip: `${PREFIX}-tooltip`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.errorContainer}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: 100,
    padding: "40px",
  },
  [`&.${classes.loadingOverlay}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
}));

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  [`& .${classes.actions}`]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  [`&.${classes.dataGrid}`]: {
    backgroundColor: theme.palette.background.paper,
    "& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeaderTitle": {
      fontWeight: "bold",
    },
    "& .MuiDataGrid-cell": {
      outline: "none !important",
      padding: "16px",
    },
    "& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox)": {
      outline: "none !important",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  [`& .${classes.tableCell}`]: {
    cursor: "pointer",
    maxWidth: 400,
    wordWrap: "break-word",
    "& pre": {
      whiteSpace: "pre-wrap",
      color: theme.palette.text.primary,
    },
    "&:hover $tableCellAction": {
      display: "initial",
    },
  },
  [`& .${classes.tooltip}`]: {
    boxShadow: theme.shadows[4],
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
  },
}));

const Root = styled(Box)(({ theme }) => ({
  [`& .${classes.paginationContainer}`]: {
    height: 50,
  },
}));

const AGGREGATION_COLUMNS = [
  {
    field: "count", // Also `grouped_count`
    flex: 0.3,
    headerName: "Count",
    sortable: false,
  },
  {
    field: "mAvgSecondsToStatus",
    flex: 1,
    headerName: "Average Time",
    sortable: false,
  },
];

const AGGREGATION_FIELDS = ["count", "grouped_count", "mAvgSecondsToStatus"];

// Get an aggregation param from a param. Due to some legacy code
// mistakes, aggregations can be either in the `field`s or `operator`s.
// To settle on a common structure, get the `operator` only variant.
export const getAggregation = (param) => {
  if (
    AGGREGATION_FIELDS.includes(param.field) ||
    AGGREGATION_FIELDS.includes(param.operator)
  ) {
    const newParam = { ...param };
    if (param.field && !param.operator) {
      newParam.operator = newParam.field;
      delete newParam.field;
      return newParam;
    } else {
      return newParam;
    }
  }
  return null;
};

// Get matching columns for any params with an aggregation field
const getAggregationColumns = (params) => {
  const aggFields = getAggregationFields(params);
  return AGGREGATION_COLUMNS.filter((c) => aggFields.includes(c.field));
};

// Get fields from `outputFields` that are aggregation fields
const getAggregationFields = (params) => {
  const outputParam = params.find((p) => p.field === "outputFields");
  const aggFields = [];
  if (outputParam) {
    for (const field of outputParam.value) {
      if (AGGREGATION_FIELDS.includes(field)) {
        aggFields.push(field === "grouped_count" ? "count" : field);
      }
    }
  }
  return aggFields;
};

export const hasAggregationFields = (params) => {
  const fields = getAggregationFields(params);
  return fields && fields.length > 0;
};

const renderActions = (actions, params) => {
  return actions.map((action) => (
    <Tooltip
      key={action.tooltip}
      title={action.tooltip}
      classes={{ tooltip: classes.tooltip }}
    >
      <IconButton
        datacy={`simpleTableRowIconButton`}
        className={classes.actions}
        color={action.color || "default"}
        onClick={(event) => {
          event.stopPropagation(); // Avoid onRowClick handler from firing
          action.onClick(params, event);
        }}
      >
        <action.icon fontSize="small" />
      </IconButton>
    </Tooltip>
  ));
};

// Get rows per page from either local storage or the first option
export const getRowsPerPage = (pageSizeOptions, uniqueName) => {
  const pageSize = parseInt(localStorage.getItem(`rowsPerPage-${uniqueName}`));
  if (pageSize) {
    return pageSize;
  } else {
    return pageSizeOptions[0];
  }
};

// Remove html and newlines from rows of data since `DataGrid` does
// not support html within rows.
export const sanitize = (rows) => {
  const parser = new DOMParser();

  return rows.map((row) => {
    const sanitizedRow = { ...row };

    for (const key of Object.keys(sanitizedRow)) {
      if (typeof sanitizedRow[key] === "string") {
        try {
          // Parse HTML and extract text
          const doc = parser.parseFromString(
            `<div>${sanitizedRow[key]}</div>`,
            "text/html"
          );
          let cleanText = doc.body.textContent || "";

          // Handle newlines if needed
          cleanText = cleanText.replace(/\n/g, " ").replace(/\s+/g, " ").trim();

          sanitizedRow[key] = cleanText;
        } catch (error) {
          console.warn(`Error sanitizing field ${key}:`, error);
          sanitizedRow[key] = sanitizedRow[key] || "";
        }
      }
    }

    return sanitizedRow;
  });
};

// Convert sortModel to orderBy for FEA
const getOrderBy = (sortModel, outputParam, params) => {
  const sortFields = [];
  for (const item of sortModel) {
    const sortDir = item.sort === "desc" ? "-" : "";
    const sortField = item.field;

    if (!outputParam || outputParam.value.includes(sortField)) {
      sortFields.push(`${sortDir}${sortField}`);
    }
  }
  if (!hasAggregationFields(params)) {
    // Always sort at least by id so that pages don't reorder
    sortFields.push("id");
  }
  return sortFields.join(",");
};

// Convert request-style params to axios-style params
// TODO we should reuse the functionality in `Request.compile()`
// instead of duplicating this.
export const compileParams = (params, sortable, sortModel) => {
  const outputParam = params.find((p) => p.field === "outputFields");
  if (outputParam) {
    outputParam.value = outputParam.value.filter((f) => f !== "id");
    if (!hasAggregationFields(params)) {
      outputParam.value.push("id");
    }
  }

  const compiled = {};
  for (const param of params) {
    const op = param?.operator;
    let value = param?.value ?? "";
    value = Array.isArray(value) ? value.join(",") : String(value);
    if (op) {
      if (param.field) {
        const negate = param?.negate ? "!" : "";
        compiled[`${param.field}.${negate}${op}`] = value;
      } else {
        compiled[`${op}`] = value;
      }
    } else {
      compiled[`${param.field}`] = value;
    }
  }

  if (sortable) {
    const orderBy = getOrderBy(sortModel, outputParam, params);
    if (orderBy) {
      compiled.orderBy = orderBy;
    }
  }

  return compiled;
};

const ServerTable = ({
  actions,
  checkboxSelectionActions,
  columns,
  contextMenuItems,
  datacy,
  defaultSort,
  endpoint,
  formatData,
  onCheckboxSelectionChange,
  onRowClick,
  refresh,
  pageSizeOptions,
  params,
  sortable,
  uniqueName,
  orgId,
}) => {
  const cols = columns.map((column) => {
    const baseColumn = {
      cellClassName: classes.tableCell,
      headerName: column.headerName || column.title,
      field: column.field,
      flex: column.flex,
      maxWidth: column.maxWidth,
      minWidth: column.minWidth,
      sortable:
        sortable && (column.sortable === undefined ? true : column.sortable),
      type: column.type || "string",
      valueGetter: column.valueGetter,
      width: column.width,
    };

    // Only add renderCell if it's defined, this avoids a weird bug
    // where boolean cells aren't displaying correctly.
    if (column.renderCell !== undefined) {
      baseColumn.renderCell = column.renderCell;
    }

    return baseColumn;
  });

  const aggColumns = getAggregationColumns(params);
  for (const col of aggColumns) {
    cols.unshift(col);
  }

  if (actions.length) {
    cols.push({
      field: "actions",
      headerName: "",
      align: "right",
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (parms) => renderActions(actions, parms),
    });
  }

  // A row can either be "clicked" or "selected". Clicked will show
  // a context menu if available. Selected will work with checkboxes
  // in the left column of the table.
  const [clickedRow, setClickedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: getRowsPerPage(pageSizeOptions, uniqueName),
  });
  const [sortModel, setSortModel] = useState(
    defaultSort || [{ field: "id", sort: "asc" }]
  );

  // Fetch all ids even for items not on the current page
  const fetchAllIds = async () => {
    setLoading(true);
    const compiled = {
      ...compileParams(params, sortable, sortModel),
      outputFields: "id",
      page: "1:5000",
    };
    try {
      const api = new API();
      const response = await api.get("finding", compiled, []);
      const data = response?.data || [];
      return data.map((finding) => finding.id);
    } catch (err) {
      console.error('Failed to fetch items for "select all"');
    } finally {
      setLoading(false);
    }
    return [];
  };

  // Fetch a page of items
  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);

    let compiled = {
      ...compileParams(params, sortable, sortModel),
      page: `${paginationModel.page + 1}:${paginationModel.pageSize}`,
    };

    try {
      const api = new API();
      const response = await api.get(endpoint, compiled, []);
      let data = response.data || [];
      if (formatData) {
        data = formatData(data);
      }
      setRows(data);
      setTotalRows(response?.meta?.totalItems || 0);
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [
    endpoint,
    paginationModel.page,
    paginationModel.pageSize,
    params,
    refresh,
    sortModel,
  ]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows, orgId]);

  // Reset the selected ids if the filters change. We need to do this
  // because filters change the items off page that _can_ be selected.
  useEffect(() => {
    setSelectedIds([]);
  }, [endpoint, params, refresh]);

  // Custom loading overlay
  const CustomLoadingOverlay = () => {
    return (
      <StyledBox className={classes.loadingOverlay}>
        <CircularProgress />
      </StyledBox>
    );
  };

  // Custom error component
  const CustomNoRowsOverlay = () => {
    if (error) {
      return (
        <StyledBox className={classes.errorContainer}>
          <Alert
            severity="error"
            sx={{ width: "100%", marginBottom: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => fetchRows()}>
                Retry
              </Button>
            }
          >
            Error loading data: {error}
          </Alert>
        </StyledBox>
      );
    } else {
      return (
        <StyledBox className={classes.errorContainer}>
          <p>No rows</p>
        </StyledBox>
      );
    }
  };

  const handlePaginationModelChange = useCallback((newPageModel) => {
    if (uniqueName && paginationModel.pageSize !== newPageModel.pageSize) {
      localStorage.setItem(`rowsPerPage-${uniqueName}`, newPageModel.pageSize);
    }
    setPaginationModel(newPageModel);
  }, []);

  const handleRowClick = useCallback(
    (params, event, details) => {
      if (onRowClick) {
        onRowClick(params);
      } else if (contextMenuItems) {
        setClickedRow({ params, event, details });
      }
    },
    [contextMenuItems, onRowClick]
  );

  const handleRowSelectionModelChange = useCallback((ids, details) => {
    setSelectedIds(ids);
    onCheckboxSelectionChange(ids);
  });

  const handleSelectionControlChange = useCallback((ids) => {
    setSelectedIds(ids);
    onCheckboxSelectionChange(ids);
  });

  const handleSortModelChange = useCallback((newSortModel) => {
    setSortModel(newSortModel);
  });

  // Note: DataGrid gets its height and width from its parent
  return (
    <Root sx={{ width: "100%" }} datacy={datacy || "simpleTable"}>
      {checkboxSelectionActions && (
        <div className={classes.paginationContainer}>
          {!!rows.length && !!selectedIds.length && (
            <ServerSelectionControls
              checkboxSelectionActions={checkboxSelectionActions}
              fetchAllIds={fetchAllIds}
              onSelectionControlChange={handleSelectionControlChange}
              pageSize={paginationModel.pageSize}
              selectedIds={selectedIds}
              totalItems={totalRows}
            />
          )}
        </div>
      )}
      <StyledDataGrid
        autoHeight
        checkboxSelection={!!checkboxSelectionActions}
        className={classes.dataGrid}
        columns={cols}
        components={{
          LoadingOverlay: CustomLoadingOverlay,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        disableColumnFilter
        disableColumnMenu
        disableColumnReorder
        disableRowSelectionOnClick
        error={error}
        getRowHeight={() => "auto"}
        getRowId={(row) => row?.id || Math.random().toString(36).slice(2)}
        hideFooterSelectedRowCount={true /* TODO get working with select all */}
        keepNonExistentRowsSelected
        loading={loading}
        onPaginationModelChange={handlePaginationModelChange}
        onRowClick={handleRowClick}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        onSortModelChange={handleSortModelChange}
        pageSizeOptions={pageSizeOptions}
        pagination={true}
        paginationMode="server"
        paginationModel={paginationModel}
        rowCount={totalRows}
        rowSelectionModel={selectedIds}
        rows={rows}
        slots={{ noRowsOverlay: CustomNoRowsOverlay }}
        sortable={sortable}
        sortingMode="server"
        sortingOrder={["asc", "desc"]}
      />
      <ServerRowMenu
        clickedRow={clickedRow}
        contextMenuItems={contextMenuItems}
        onClose={() => setClickedRow(null)}
      />
    </Root>
  );
};

ServerTable.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.object),
  checkboxSelectionActions: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  contextMenuItems: PropTypes.arrayOf(PropTypes.object),
  datacy: PropTypes.string,
  defaultSort: PropTypes.arrayOf(PropTypes.object),
  endpoint: PropTypes.string.isRequired,
  formatData: PropTypes.func,
  onCheckboxSelectionChange: PropTypes.func,
  onRowClick: PropTypes.func,
  refresh: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  params: PropTypes.arrayOf(PropTypes.object),
  sortable: PropTypes.bool,
  uniqueName: PropTypes.string.isRequired, // App-wide unique name
};

ServerTable.defaultProps = {
  actions: [],
  params: [],
  pageSizeOptions: [25, 50, 100, 250],
  sortable: false, // Not all of our APIs allow order_by yet
};

export default ServerTable;
